/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import type { KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography'
import { ticketColors } from '../../shared/theme/ticketColors';
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { ModalFormSection } from '../../shared/modals/ModalFormSection'
import { ModalShell } from '../../shared/modals/ModalShell'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { ModalNestedPicker } from '../../shared/modals/ModalNestedPicker';
import { Ionicons } from '@expo/vector-icons';
import { isValidCuitDigits, normalizeCuitDigits } from '../../fiscal/domain-api';
import { consultaPadron } from '../../fiscal/infra/padronAdapter';
import { WEB_SIDEBAR_BREAKPOINT } from '../../shared/layout/breakpoints';

const TAX_CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: 'consumidor_final', label: 'Consumidor Final' },
  { value: 'monotributo', label: 'Monotributista' },
  { value: 'responsable_inscripto', label: 'Responsable Inscripto' },
  { value: 'exento', label: 'Exento' },
];

type Props = {
  visible: boolean;
  narrowActions: boolean;
  editingCustomer: { id: string; name: string } | null;
  formName: string;
  formPhone: string;
  formCuit: string;
  formLimit: string;
  formEmail: string;
  formAddress: string;
  formTaxCategory: string;
  formNotes: string;
  formPaymentTermsDays: string;
  formPriceListId: string;
  priceListOptions: Array<{ value: string; label: string }>;
  showPriceListField: boolean;
  /** Capability customer_credit_fiado — muestra límite de crédito y días de pago. */
  showCreditFields: boolean;
  savingCustomerForm: boolean;
  discardConfirmVisible: boolean;
  duplicateWarning?: { id: string; name: string; phone: string }[] | null;
  onFormNameChange: (v: string) => void;
  onFormPhoneChange: (v: string) => void;
  onFormCuitChange: (v: string) => void;
  onFormLimitChange: (v: string) => void;
  onFormEmailChange: (v: string) => void;
  onFormAddressChange: (v: string) => void;
  onFormTaxCategoryChange: (v: string) => void;
  onFormNotesChange: (v: string) => void;
  onFormPaymentTermsDaysChange: (v: string) => void;
  onFormPriceListIdChange: (v: string) => void;
  onTryClose: () => void;
  onCloseDiscardConfirm: () => void;
  onDiscard: () => void;
  onSave: () => void;
  onConfirmCreateAnyway?: () => void;
  onDismissDuplicateWarning?: () => void;
  branchId?: string | null;
};

export function CustomerEditorModal({
  visible,
  narrowActions,
  editingCustomer,
  formName,
  formPhone,
  formCuit,
  formLimit,
  formEmail,
  formAddress,
  formTaxCategory,
  formNotes,
  formPaymentTermsDays,
  formPriceListId,
  priceListOptions,
  showPriceListField,
  showCreditFields,
  savingCustomerForm,
  discardConfirmVisible,
  duplicateWarning,
  onFormNameChange,
  onFormPhoneChange,
  onFormCuitChange,
  onFormLimitChange,
  onFormEmailChange,
  onFormAddressChange,
  onFormTaxCategoryChange,
  onFormNotesChange,
  onFormPaymentTermsDaysChange,
  onFormPriceListIdChange,
  onTryClose,
  onCloseDiscardConfirm,
  onDiscard,
  onSave,
  onConfirmCreateAnyway,
  onDismissDuplicateWarning,
  branchId = null,
}: Props) {
  const [consultingArca, setConsultingArca] = useState(false);
  const [arcaError, setArcaError] = useState<string | null>(null);
  const [arcaSandbox, setArcaSandbox] = useState(false);
  const [priceListPickerOpen, setPriceListPickerOpen] = useState(false);
  const selectedPriceListLabel =
    priceListOptions.find((opt) => opt.value === formPriceListId)?.label ?? 'Sin lista';

  // Desktop (~860px wide): pares de campos en 2 columnas. Mobile: apilado.
  const { width: winW } = useWindowDimensions();
  const isWide = winW >= WEB_SIDEBAR_BREAKPOINT;
  const rowStyle = isWide ? editorStyles.fieldRow : undefined;
  const colStyle = isWide ? editorStyles.fieldCol : undefined;

  useEffect(() => {
    if (visible) {
      setArcaError(null);
      setArcaSandbox(false);
      setConsultingArca(false);
      requestAnimationFrame(() => scrollRef.current?.scrollTo({ y: 0, animated: false }));
    }
  }, [visible, editingCustomer?.id]);

  const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const scrollContentRef = useRef<View>(null);
  const nameInputRef = useRef<TextInput>(null);
  const phoneInputRef = useRef<TextInput>(null);
  const cuitInputRef = useRef<TextInput>(null);
  const limitInputRef = useRef<TextInput>(null);
  const emailInputRef = useRef<TextInput>(null);
  const addressInputRef = useRef<TextInput>(null);
  const paymentTermsInputRef = useRef<TextInput>(null);
  const notesInputRef = useRef<TextInput>(null);

  const handleConsultarArca = async () => {
    if (!branchId) {
      setArcaError('Seleccioná una sucursal activa.');
      return;
    }
    if (!isValidCuitDigits(formCuit)) {
      setArcaError('Ingresá un CUIT de 11 dígitos.');
      return;
    }
    setConsultingArca(true);
    setArcaError(null);
    setArcaSandbox(false);
    try {
      const result = await consultaPadron(normalizeCuitDigits(formCuit), branchId);
      if (!formName.trim() || !editingCustomer) {
        onFormNameChange(result.razonSocial);
      }
      onFormCuitChange(result.cuit);
      setArcaSandbox(Boolean(result.sandbox));
    } catch (err: unknown) {
      setArcaError(err instanceof Error ? err.message : 'No se pudo consultar ARCA.');
    } finally {
      setConsultingArca(false);
    }
  };

  const actionRow = narrowActions ? posUi.modalActionsStack : posUi.modalActions;
  const secondaryBtn = narrowActions
    ? [posUi.secondaryButton, posUi.modalActionFullWidth]
    : [posUi.secondaryButton, posUi.modalButtonHalf];
  const primaryBtn = narrowActions
    ? [posUi.primaryButton, posUi.modalActionFullWidth]
    : [posUi.primaryButton, posUi.modalButtonHalf];

  const discardFooter = (
    <View style={actionRow}>
      <TouchableOpacity
        style={secondaryBtn}
        onPress={onCloseDiscardConfirm}
        accessibilityRole="button"
        accessibilityLabel="Seguir editando"
      >
        <Text style={posUi.secondaryButtonText}>Seguir editando</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[...secondaryBtn, { borderColor: ticketColors.bad }]}
        onPress={onDiscard}
        accessibilityRole="button"
        accessibilityLabel="Descartar cambios"
      >
        <Text style={[posUi.secondaryButtonText, { color: ticketColors.bad }]}>Descartar</Text>
      </TouchableOpacity>
    </View>
  );

  const duplicateFooter = (
    <View style={actionRow}>
      <TouchableOpacity
        style={secondaryBtn}
        onPress={onDismissDuplicateWarning}
        disabled={savingCustomerForm}
        accessibilityRole="button"
        accessibilityLabel="Cancelar y seguir editando"
      >
        <Text style={posUi.secondaryButtonText}>Seguir editando</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[...primaryBtn, savingCustomerForm && posUi.disabledCard]}
        onPress={onConfirmCreateAnyway}
        disabled={savingCustomerForm}
        accessibilityRole="button"
        accessibilityLabel="Crear de todos modos"
      >
        {savingCustomerForm ? (
          <ActivityIndicator color="#FFFFFF" />
        ) : (
          <Text style={posUi.primaryButtonText}>Crear de todos modos</Text>
        )}
      </TouchableOpacity>
    </View>
  );

  const editorFooter = (
    <View style={actionRow}>
      <TouchableOpacity
        style={secondaryBtn}
        onPress={onTryClose}
        disabled={savingCustomerForm}
        accessibilityRole="button"
        accessibilityLabel="Cancelar"
      >
        <Text style={posUi.secondaryButtonText}>Cancelar</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[...primaryBtn, savingCustomerForm && posUi.disabledCard]}
        onPress={onSave}
        disabled={savingCustomerForm}
        accessibilityRole="button"
        accessibilityLabel="Guardar"
      >
        {savingCustomerForm ? (
          <ActivityIndicator color="#FFFFFF" />
        ) : (
          <Text style={posUi.primaryButtonText}>Guardar</Text>
        )}
      </TouchableOpacity>
    </View>
  );

  const showDuplicateWarning = !discardConfirmVisible && duplicateWarning && duplicateWarning.length > 0;

  return (
    <ModalShell
      visible={visible}
      onClose={discardConfirmVisible ? onCloseDiscardConfirm : showDuplicateWarning ? (onDismissDuplicateWarning ?? onTryClose) : onTryClose}
      title={
        discardConfirmVisible
          ? '¿Descartar cambios?'
          : showDuplicateWarning
            ? 'Posible duplicado'
            : editingCustomer
              ? `Editar · ${editingCustomer.name}`
              : 'Agregar cliente'
      }
      closeAccessibilityLabel="Cerrar"
      appearance="ticket"
      kicker="Clientes"
      subtitle={
        discardConfirmVisible
          ? undefined
          : showDuplicateWarning
            ? 'Ya hay clientes con ese nombre o teléfono.'
            : editingCustomer
              ? 'Actualizá el contacto, el CUIT y el límite.'
              : 'Con nombre y teléfono alcanza. El resto suma para cobrar y facturar.'
      }
      widthVariant="wide"
      scrollable={!discardConfirmVisible && !showDuplicateWarning}
      scrollRef={scrollRef}
      contentRef={scrollContentRef}
      scrollBottomInset={spacing.sm}
      scrollContentStyle={modalExperience.formStack}
      footer={discardConfirmVisible ? discardFooter : showDuplicateWarning ? duplicateFooter : editorFooter}
    >
      {discardConfirmVisible ? (
        <View style={modalExperience.warningPanel}>
          <Text style={posUi.checkoutHint}>Tenés cambios sin guardar. Si salís ahora los perdés.</Text>
        </View>
      ) : showDuplicateWarning ? (
        <View style={modalExperience.warningPanel} testID="customers.editor.duplicate-warning">
          <Text style={posUi.checkoutHint}>
            Encontramos {duplicateWarning!.length} cliente{duplicateWarning!.length > 1 ? 's' : ''} con datos similares:
          </Text>
          {duplicateWarning!.map((dupe) => (
            <View key={dupe.id} style={duplicateStyles.row}>
              <Text style={duplicateStyles.name} numberOfLines={1}>{dupe.name}</Text>
              {dupe.phone ? (
                <Text style={duplicateStyles.phone} numberOfLines={1}>{dupe.phone}</Text>
              ) : null}
            </View>
          ))}
          <Text style={[posUi.checkoutHint, { marginTop: spacing.sm }]}>
            Si es la misma persona, abrí su ficha en lugar de crear una nueva. Si es un cliente distinto, podés crearlo de todos modos.
          </Text>
        </View>
      ) : (
        <View style={editorStyles.formBody}>
          <ModalFormSection title="Datos">
            <View style={rowStyle}>
              <ModalFormField label="Nombre" required style={colStyle}>
                <TextInput
                  ref={nameInputRef}
                  style={modalExperience.formInput}
                  value={formName}
                  onChangeText={onFormNameChange}
                  placeholder="Nombre o razón social"
                  placeholderTextColor={colors.inputPlaceholder}
                />
              </ModalFormField>
              <ModalFormField label="Teléfono" style={colStyle}>
                <View style={phonePrefixStyles.row}>
                  <Text style={phonePrefixStyles.prefix}>+54</Text>
                  <NumericTextInput
                    ref={phoneInputRef}
                    kind="phone"
                    style={[modalExperience.formInput, phonePrefixStyles.input]}
                    value={formPhone}
                    onChangeText={onFormPhoneChange}
                    placeholder="11 5555-5555"
                    placeholderTextColor={colors.inputPlaceholder}
                  />
                </View>
              </ModalFormField>
            </View>
            <View style={rowStyle}>
              <ModalFormField label="CUIT" style={colStyle}>
                <NumericTextInput
                  ref={cuitInputRef}
                  kind="digits"
                  style={modalExperience.formInput}
                  value={formCuit}
                  onChangeText={onFormCuitChange}
                  placeholder="CUIT"
                  placeholderTextColor={colors.inputPlaceholder}
                />
              </ModalFormField>
              <View style={colStyle}>
                <ModalFormField label=" ">
                  <TouchableOpacity
                    style={[posUi.secondaryButton, consultingArca && posUi.disabledCard]}
                    onPress={() => void handleConsultarArca()}
                    disabled={consultingArca || savingCustomerForm}
                    accessibilityRole="button"
                    accessibilityLabel="Consultar datos en ARCA"
                  >
                    {consultingArca ? (
                      <ActivityIndicator color={ticketColors.ink} />
                    ) : (
                      <Text style={posUi.secondaryButtonText}>Consultar ARCA</Text>
                    )}
                  </TouchableOpacity>
                  <Text style={editorStyles.fieldHint}>Trae razón social desde el padrón.</Text>
                </ModalFormField>
                {arcaSandbox ? (
                  <Text style={editorStyles.fieldHint}>Modo prueba, el nombre no es el de ARCA.</Text>
                ) : null}
                {arcaError ? (
                  <View style={modalExperience.warningPanel}>
                    <Text style={modalExperience.stateText}>{arcaError}</Text>
                  </View>
                ) : null}
              </View>
            </View>
          </ModalFormSection>

          {showCreditFields || showPriceListField ? (
            <ModalFormSection title="Comercial" divided>
              <View style={rowStyle}>
                {showCreditFields ? (
                  <ModalFormField label="Límite de crédito" style={colStyle}>
                    <NumericTextInput
                      ref={limitInputRef}
                      kind="decimalMoney"
                      style={modalExperience.formInput}
                      value={formLimit}
                      onChangeText={onFormLimitChange}
                      placeholder="Sin límite"
                      placeholderTextColor={colors.inputPlaceholder}
                    />
                  </ModalFormField>
                ) : null}
                {showPriceListField ? (
                  <ModalFormField label="Lista de precio" style={colStyle}>
                    <TouchableOpacity
                      style={modalExperience.entitySelector}
                      onPress={() => setPriceListPickerOpen(true)}
                      accessibilityRole="button"
                      accessibilityLabel={`Lista de precio: ${selectedPriceListLabel}`}
                      testID="customers.editor.price-list.open-picker"
                    >
                      <Text style={modalExperience.filterSelectValue} numberOfLines={1}>
                        {selectedPriceListLabel}
                      </Text>
                      <Ionicons name="chevron-down" size={16} color={ticketColors.ink3} />
                    </TouchableOpacity>
                  </ModalFormField>
                ) : null}
                {showCreditFields ? (
                  <ModalFormField label="Días de pago" style={colStyle}>
                    <NumericTextInput
                      ref={paymentTermsInputRef}
                      kind="digits"
                      style={modalExperience.formInput}
                      value={formPaymentTermsDays}
                      onChangeText={onFormPaymentTermsDaysChange}
                      placeholder="0"
                      placeholderTextColor={colors.inputPlaceholder}
                    />
                  </ModalFormField>
                ) : null}
              </View>
              {showCreditFields ? (
                <Text style={editorStyles.fieldHint}>
                  Si pasa el límite al cargar, un supervisor puede autorizar con PIN.
                </Text>
              ) : null}
            </ModalFormSection>
          ) : null}

          <ModalFormSection title="Contacto y factura" divided>
            <View style={rowStyle}>
              <ModalFormField label="Email" style={colStyle}>
                <TextInput
                  ref={emailInputRef}
                  style={modalExperience.formInput}
                  value={formEmail}
                  onChangeText={onFormEmailChange}
                  placeholder="email@ejemplo.com"
                  placeholderTextColor={colors.inputPlaceholder}
                  keyboardType="email-address"
                  autoCapitalize="none"
                />
              </ModalFormField>
              <ModalFormField label="Dirección" style={colStyle}>
                <TextInput
                  ref={addressInputRef}
                  style={modalExperience.formInput}
                  value={formAddress}
                  onChangeText={onFormAddressChange}
                  placeholder="Calle 123, Ciudad"
                  placeholderTextColor={colors.inputPlaceholder}
                />
              </ModalFormField>
            </View>
            <ModalFormField label="Categoría IVA">
              <View style={editorStyles.taxRow}>
                {TAX_CATEGORY_OPTIONS.map((opt) => (
                  <TouchableOpacity
                    key={opt.value}
                    style={[editorStyles.taxChip, formTaxCategory === opt.value && editorStyles.taxChipActive]}
                    onPress={() => onFormTaxCategoryChange(opt.value)}
                    accessibilityRole="radio"
                    accessibilityLabel={opt.label}
                    accessibilityState={{ checked: formTaxCategory === opt.value }}
                  >
                    <Text
                      style={[
                        editorStyles.taxChipText,
                        formTaxCategory === opt.value && editorStyles.taxChipTextActive,
                      ]}
                    >
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ModalFormField>
            <ModalFormField label="Notas">
              <TextInput
                ref={notesInputRef}
                style={[modalExperience.formInput, editorStyles.notesInput]}
                value={formNotes}
                onChangeText={onFormNotesChange}
                placeholder="Notas internas"
                placeholderTextColor={colors.inputPlaceholder}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />
            </ModalFormField>
          </ModalFormSection>
        </View>
      )}
      <ModalNestedPicker
        visible={priceListPickerOpen}
        title="Lista de precio"
        options={[
          { value: '', label: 'Sin lista' },
          ...priceListOptions,
        ]}
        selectedValue={formPriceListId}
        onSelect={(value) => onFormPriceListIdChange(value)}
        onClose={() => setPriceListPickerOpen(false)}
      />
    </ModalShell>
  );
}

const editorStyles = StyleSheet.create({
  // Desktop (~860px wide): pares de campos en 2 columnas; mobile queda apilado.
  fieldRow: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  fieldCol: {
    flex: 1,
    minWidth: 0,
  },
  taxRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    paddingTop: 2,
  },
  fieldHint: {
    fontSize: 12,
    fontWeight: '500',
    color: ticketColors.ink3,
    marginTop: spacing.xs,
  },
  formBody: {
    gap: spacing.md,
  },
  taxChip: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
  },
  taxChipActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold + '22',
  },
  taxChipText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
  taxChipTextActive: {
    color: ticketColors.gold,
    fontWeight: '800',
  },
  notesInput: {
    minHeight: 72,
    paddingTop: spacing.xs,
  },
});

const phonePrefixStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: spacing.xs,
  },
  prefix: {
    ...typography.caption,
    alignSelf: 'center',
    color: ticketColors.ink3,
    fontWeight: '700',
    paddingHorizontal: spacing.sm,
  },
  input: {
    flex: 1,
    minWidth: 0,
  },
});

const duplicateStyles = StyleSheet.create({
  row: {
    paddingVertical: spacing.xs,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  name: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  phone: {
    ...typography.caption,
    color: ticketColors.ink3,
    marginTop: 2,
  },
});
